import { useCallback, useState } from 'react';
import { TemplateDocx } from '../../types/templates.types';
import { DOCX_MIME, FillVariable } from './utils';
import DocxFillForm from './docx-fill-flow/docx-fill-form';
import DocxLoadingScreen from './docx-fill-flow/docx-loading-screen';
import { useDocxGeneration } from './hooks/useDocxGeneration';
import { mergeDocx } from './merge-docx';

interface DocxTemplateGenerateFlowProps {
  templateId: string;
  slug: string;
  title: string;
  description: string;
  isPreviewMode: boolean;
  variables: FillVariable[];
  docx: TemplateDocx;
}

export default function DocxTemplateGenerateFlow({
  variables,
  title,
  isPreviewMode,
  slug,
  description,
  docx,
  templateId,
}: DocxTemplateGenerateFlowProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());
  const [formError, setFormError] = useState<string | null>(null);

  // Merge the filled values into the tokenized template and return the .docx blob.
  // Deterministic, browser-side, no AI. Throws on readiness/merge failure.
  //
  // No access check here — `DocxTemplateGeneratePage` gates the whole page on
  // membership in the template's org before this ever renders. The check that
  // used to live here compared against the *active* org, so a member of two
  // orgs failed on their own template unless they'd switched first.
  const merge = useCallback(async (): Promise<Blob> => {
    if (docx.status !== 'ready' || !docx.tokenizedUrl) {
      throw new Error("This template isn't ready to use yet.");
    }

    const merged = await mergeDocx({ tokenizedUrl: docx.tokenizedUrl, variables, values });
    return new Blob([merged as BlobPart], { type: DOCX_MIME });
  }, [values, variables, docx]);

  const generation = useDocxGeneration({ merge, values, title, slug, templateId });

  // While generating (or on a generation error), the whole view swaps to the status screen.
  if (generation.isGenerating || generation.error) {
    return (
      <DocxLoadingScreen
        steps={generation.steps}
        stepIndex={generation.stepIndex}
        progress={generation.progress}
        error={generation.error}
        onRetry={generation.retry}
        onBack={generation.reset}
      />
    );
  }

  return (
    <DocxFillForm
      slug={slug}
      description={description}
      title={title}
      fieldErrors={fieldErrors}
      formError={formError}
      isPreviewMode={isPreviewMode}
      previewLoading={false}
      onGenerate={generation.generate}
      setFieldErrors={setFieldErrors}
      setFormError={setFormError}
      setValues={setValues}
      values={values}
      variables={variables}
    />
  );
}
